/**
 * `/v1/company-prefixes` — the request shapes.
 *
 * Only the evidence submission lives here. `POST /v1/company-prefixes` still parses by hand
 * in `apps/api/src/routes/company-prefixes.ts`, so `claimCompanyPrefixSchema` below binds
 * the tRPC transport alone.
 */
import { z } from "zod";
import { VERIFICATIONS } from "../verification.js";
import { COMPANY_PREFIX } from "./patterns.js";
/**
 * What a submitted document is.
 *
 * One member, and it is the one §12 names: *"fallback = document verification (GS1 licence
 * certificate upload, human review)"*. Enumerated rather than free text for the reason
 * every vocabulary in this package is — a reviewer's queue filters on it, and a second
 * spelling of one kind is two queues.
 */
export const PREFIX_EVIDENCE_KINDS = ["licence_certificate"];
const KIND_MESSAGE = `kind must be one of: ${PREFIX_EVIDENCE_KINDS.join(", ")}.`;
const MIME_MESSAGE = "mime is required. A GS1 licence certificate is normally a PDF or a photograph of one.";
const BYTES_MESSAGE = "bytes must be a whole number of bytes, zero or more. It is the size you are about to " +
    "upload; the file itself never passes through this API.";
/**
 * `POST /v1/company-prefixes/{id}/verify` — submit evidence for human review (§5, §12).
 *
 * ## Submitting is not verifying, and the endpoint is named for the act the customer takes
 *
 * §5 spells this endpoint *"POST /{id}/verify (registry check or evidence upload)"*, and
 * the name is kept. What it must never do is **grant** verification: §12's ladder is claim
 * → evidence → human review → verified, and invariant 4 is that *"a paid plan never
 * shortcuts it"*. A customer-callable endpoint that set `verified_document` would be that
 * invariant broken at its own door — so this one moves `unverified` to `pending` and
 * nothing else. The registry-check arm §5 also names is Phase 8's (§15.1): nothing produces
 * `verified_registry` yet, and this schema deliberately has no member that would.
 *
 * Like the presign it rides on, it names no storage key. See `api/media.ts`.
 */
export const submitPrefixEvidenceSchema = z
    .object({
    kind: z.enum(PREFIX_EVIDENCE_KINDS, KIND_MESSAGE).default("licence_certificate"),
    mime: z.string(MIME_MESSAGE).min(1, MIME_MESSAGE),
    bytes: z.number(BYTES_MESSAGE).int(BYTES_MESSAGE).min(0, BYTES_MESSAGE),
    /** Shown to the reviewer; never part of the object key. */
    filename: z.string().max(255).optional(),
    /** Anything the claimant wants the reviewer to read. */
    note: z.string().max(2000).optional(),
})
    .describe("POST /v1/company-prefixes/{id}/verify — returns a presigned PUT.");
const PREFIX_MESSAGE = "prefix must be 4–12 digits";
/**
 * `companyPrefixes.claim` — tRPC only, and the two transports answer a malformed prefix
 * with different codes: this schema gives `VALIDATION_FAILED`, REST gives `INVALID_PREFIX`.
 * Owed observation and its owner: Drift Log §U66.
 */
export const claimCompanyPrefixSchema = z
    .object({
    prefix: z.string(PREFIX_MESSAGE).trim().regex(COMPANY_PREFIX, PREFIX_MESSAGE),
    gs1Mo: z.string().max(100).optional(),
})
    .strict();
export const companyPrefixClaimedSchema = z.object({
    id: z.uuid(),
    prefix: z.string(),
    gs1Mo: z.string().nullable(),
    verification: z.enum(VERIFICATIONS),
});
/** `storageKey` is on the op's result and deliberately not here: the SPA never needs a key. */
export const prefixEvidenceSubmittedSchema = z.object({
    prefixId: z.uuid(),
    documentId: z.uuid(),
    verification: z.enum(VERIFICATIONS),
    upload: z.object({
        url: z.string(),
        method: z.literal("PUT"),
        expiresInSeconds: z.number(),
    }),
});
/** `submitEvidence`'s input over tRPC: the REST body plus the path's prefix id. */
export const submitPrefixEvidenceProcedureSchema = z
    .object({ prefixId: z.uuid() })
    .and(submitPrefixEvidenceSchema);
//# sourceMappingURL=company-prefixes.js.map